/* @flow */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from '@vis.gl/react-google-maps';

type Props = {
    position: { lat: number, lng: number },
    zIndex?: number,
    mapPaneName?: string,
    children: any,
};

/**
 * '@vis.gl/react-google-maps' deliberately does not wrap
 * `google.maps.OverlayView`, so this recreates the behaviour using the native
 * class, positioned against the map instance from `useMap()`, with the React
 * children portalled into the overlay's own container element.
 *

/**
 * Values of the old `OverlayView.*` pane constants. Kept here so call sites can
 * read them without depending on a map library just for a string.
 */
export const OVERLAY_MOUSE_TARGET = 'overlayMouseTarget';
export const FLOAT_PANE = 'floatPane';

/**
 * Tracks whether each map is mid-drag.
 *
 * One `dragstart`/`dragend` listener per map, shared by every overlay on it -
 * a cluster view can mount hundreds of overlays, and giving each one its own
 * map listener would be wasteful. Keyed weakly so it dies with the map.
 */
const dragTrackers = new WeakMap();

const getDragTracker = (map: Object) => {
    if (!map) return null;
    let tracker = dragTrackers.get(map);
    if (!tracker) {
        tracker = { isDragging: false };
        map.addListener('dragstart', () => { tracker.isDragging = true; });
        // `dragend` fires before the browser's synthetic click, so clear the
        // flag on the next tick instead - otherwise the click we want to
        // suppress arrives after the flag is already back to false.
        map.addListener('dragend', () => {
            window.setTimeout(() => { tracker.isDragging = false; }, 0);
        });
        dragTrackers.set(map, tracker);
    }
    return tracker;
};

/**
 * Builds one `google.maps.OverlayView` instance for a single overlay.
 */
const createOverlay = (container: Object, mapPaneName: string, positionRef: Object) => {
    // Local alias so the assignments inside `draw()` don't trip
    // `no-param-reassign` (which flags mutating properties of a parameter).
    const el = container;
    class Overlay extends window.google.maps.OverlayView {
        onAdd() {
            const panes = this.getPanes();
            if (!panes) return;
            const pane = panes[mapPaneName] || panes.overlayMouseTarget;
            if (pane) pane.appendChild(el);
        }

        draw() {
            const projection = this.getProjection();
            if (!projection) return;
            const { lat, lng } = positionRef.current || {};
            if (lat === undefined || lng === undefined) return;
            const point = projection.fromLatLngToDivPixel(
                new window.google.maps.LatLng(Number(lat), Number(lng)),
            );
            if (point) {
                el.style.left = `${point.x}px`;
                el.style.top = `${point.y}px`;
            }
        }

        onRemove() {
            el.remove();
        }
    }

    return new Overlay();
};

const CustomOverlayView = ({
    position,
    zIndex,
    mapPaneName = OVERLAY_MOUSE_TARGET,
    children,
}: Props) => {
    const map = useMap();
    const containerRef = useRef(null);
    const overlayRef = useRef(null);
    const positionRef = useRef(position);
    positionRef.current = position;
    const positionLat = position?.lat;
    const positionLng = position?.lng;

    if (containerRef.current === null) {
        containerRef.current = document.createElement('div');
        containerRef.current.style.position = 'absolute';
    }

    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;
        const container = containerRef.current;
        const overlay = createOverlay(container, mapPaneName, positionRef);
        overlay.setMap(map);
        overlayRef.current = overlay;
        const idleListener = map.addListener('idle', () => overlay.draw());
        const boundsListener = map.addListener('bounds_changed', () => overlay.draw());

        return () => {
            idleListener.remove();
            boundsListener.remove();
            overlay.setMap(null);
            overlayRef.current = null;
        };
    }, [map, mapPaneName]);

    useEffect(() => {
        if (overlayRef.current) overlayRef.current.draw();
    }, [positionLat, positionLng]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.zIndex = zIndex !== undefined ? String(zIndex) : '';
        }
    }, [zIndex]);

    /**
     * Swallow the click that the browser synthesises at the end of a map drag.
     *
     * Overlay content sits in a map pane, and the pane is translated along with
     * the map while the user drags. The element therefore stays under the
     * cursor for the whole gesture, so mousedown and mouseup land on the same
     * node and the browser fires a real `click` when the drag ends - which the
     * marker/cluster handlers read as a deliberate click and zoom in.
     *
     * We listen in the capture phase on the container, which is an ancestor of
     * whatever React rendered inside it, so stopping propagation here prevents
     * React's onClick from ever running.
     */
    useEffect(() => {
        if (!map) return undefined;
        const container = containerRef.current;
        if (!container) return undefined;

        const tracker = getDragTracker(map);
        let draggedSincePress = false;

        const onPointerDown = () => { draggedSincePress = false; };
        const onClickCapture = (event) => {
            if (!draggedSincePress && !tracker.isDragging) return;
            draggedSincePress = false;
            event.stopPropagation();
            event.preventDefault();
        };
        const onPointerMove = () => {
            if (tracker.isDragging) draggedSincePress = true;
        };

        container.addEventListener('mousedown', onPointerDown, true);
        container.addEventListener('touchstart', onPointerDown, true);
        container.addEventListener('mousemove', onPointerMove, true);
        container.addEventListener('click', onClickCapture, true);

        return () => {
            container.removeEventListener('mousedown', onPointerDown, true);
            container.removeEventListener('touchstart', onPointerDown, true);
            container.removeEventListener('mousemove', onPointerMove, true);
            container.removeEventListener('click', onClickCapture, true);
        };
    }, [map]);

    return createPortal(children, containerRef.current);
};

export default CustomOverlayView;
